import { useId, type ChangeEvent, type ReactNode } from 'react';

/**
 * Select — native `<select>` ベースの drop-down (Sprint AS、docs/16 §10-2 P5 設計)。
 *
 * 設計方針:
 *   - native `<select>` を採用 (a11y / mobile UX / keyboard すべて browser default に任せる)
 *   - placeholder は disabled な空 option (`<option value="" disabled>`) として表示
 *     value が空文字 + placeholder 指定時にこの option が選択状態で見える
 *   - options[].disabled に対応 (個別 option を無効化)
 *   - label / error / hint / a11y 規約は TextField と統一
 */

export interface SelectOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

export interface SelectProps {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** 値未選択時に表示するヒント文言 (空 option として挿入). */
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: ReactNode;
  hint?: ReactNode;
  id?: string;
  className?: string;
}

const SELECT_BASE =
  'block w-full rounded-md border bg-white text-slate-900 px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed';
const SELECT_NORMAL = 'border-slate-300 focus:border-primary-600 focus:ring-primary-400';
const SELECT_ERROR = 'border-danger-600 focus:border-danger-600 focus:ring-danger-600';

export function Select({
  label,
  value,
  onChange,
  options,
  placeholder,
  required,
  disabled,
  error,
  hint,
  id,
  className = '',
}: SelectProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  const invalid = Boolean(error);

  return (
    <div className={className}>
      <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1">
        {label}
        {required ? (
          <span aria-hidden="true" className="text-danger-600 ml-0.5">
            *
          </span>
        ) : null}
      </label>
      <select
        id={inputId}
        value={value}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy}
        className={`${SELECT_BASE} ${invalid ? SELECT_ERROR : SELECT_NORMAL}`}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      {error ? (
        <p id={errorId} className="mt-1 text-xs text-danger-600">
          {error}
        </p>
      ) : null}
      {hint ? (
        <p id={hintId} className="mt-1 text-xs text-slate-600">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
